import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Select, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';

const PerformancePaper = styled(Paper)({
  padding: '2rem',
  backgroundColor: '#fafafa',
  border: '1px solid #eee',
});

const SectionTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '1.8rem',
  color: '#1a1a1a',
  marginBottom: '1.5rem',
});

const StatCard = styled(Paper)({
  padding: '1.5rem',
  backgroundColor: '#fff',
  border: '1px solid #eee',
  height: '100%',
  textAlign: 'center',
});

const StatValue = styled(Typography)({
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#c4a661',
  marginBottom: '0.5rem',
});

const ChartContainer = styled(Box)({
  backgroundColor: '#fff',
  padding: '1.5rem',
  border: '1px solid #eee',
  marginTop: '2rem',
  height: 400,
  position: 'relative',
});

const ChartBar = styled(Box)(({ height }) => ({
  position: 'absolute',
  bottom: '40px',
  width: '30px',
  height: `${height}%`,
  backgroundColor: '#c4a661',
  transition: 'height 0.3s ease',
  '&:hover': {
    backgroundColor: '#a38a4a',
  },
}));

const ChartLabel = styled(Typography)({
  position: 'absolute',
  bottom: '10px',
  width: '30px',
  textAlign: 'center',
  fontSize: '0.75rem',
  color: '#666',
});

const Performance = () => {
  const [timeRange, setTimeRange] = useState('month');

  // Mock data - replace with real data from backend
  const performanceData = {
    totalEarnings: 2850,
    totalClicks: 1234,
    conversionRate: '3.65%',
    averageOrderValue: 632,
  };

  // Mock chart data
  const chartData = [
    { date: '03/15', value: 45 },
    { date: '03/16', value: 65 },
    { date: '03/17', value: 85 },
    { date: '03/18', value: 95 },
    { date: '03/19', value: 75 },
    { date: '03/20', value: 120 },
  ];

  // Mock recent sales data
  const recentSales = [
    { id: 1, date: '2025-03-20', product: 'Diamond Solitaire Ring', amount: 4999, commission: 750 },
    { id: 2, date: '2025-03-19', product: 'Pearl Necklace', amount: 2499, commission: 375 },
    { id: 3, date: '2025-03-18', product: 'Sapphire Earrings', amount: 3499, commission: 525 },
  ];

  // Calculate max value for chart scaling
  const maxValue = Math.max(...chartData.map(item => item.value));

  return (
    <PerformancePaper elevation={0}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <SectionTitle>Performance Analytics</SectionTitle>
        <Select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="week">Last Week</MenuItem>
          <MenuItem value="month">Last Month</MenuItem>
          <MenuItem value="quarter">Last Quarter</MenuItem>
          <MenuItem value="year">Last Year</MenuItem>
        </Select>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <StatValue>${performanceData.totalEarnings}</StatValue>
            <Typography color="textSecondary">Total Earnings</Typography>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <StatValue>{performanceData.totalClicks}</StatValue>
            <Typography color="textSecondary">Total Clicks</Typography>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <StatValue>{performanceData.conversionRate}</StatValue>
            <Typography color="textSecondary">Conversion Rate</Typography>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <StatValue>${performanceData.averageOrderValue}</StatValue>
            <Typography color="textSecondary">Avg Order Value</Typography>
          </StatCard>
        </Grid>
      </Grid>

      <ChartContainer>
        <Typography variant="h6" gutterBottom>Performance Trends</Typography>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          alignItems: 'flex-end',
          height: '100%',
          pt: 4,
          pb: 2,
          position: 'relative',
        }}>
          {chartData.map((item, index) => (
            <Box key={index} sx={{ position: 'relative' }}>
              <ChartBar height={(item.value / maxValue) * 80} />
              <ChartLabel>{item.date}</ChartLabel>
            </Box>
          ))}
        </Box>
      </ChartContainer>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>Recent Sales</Typography>
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Sale Amount</TableCell>
                <TableCell align="right">Commission</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentSales.map((sale) => (
                <TableRow key={sale.id}>
                  <TableCell>{sale.date}</TableCell>
                  <TableCell>{sale.product}</TableCell>
                  <TableCell align="right">${sale.amount}</TableCell>
                  <TableCell align="right">${sale.commission}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </PerformancePaper>
  );
};

export default Performance;
